// src/auth/UniversityHeadResetPassword.js

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    alpha,
    CircularProgress,
    IconButton,
    InputAdornment,
    LinearProgress
} from '@mui/material';
import {
    Lock,
    Visibility,
    VisibilityOff,
    CheckCircle,
    ArrowBack,
    AccountBalance as InstitutionHeadIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCustomToast from '../hooks/useToast';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
    APIInstitutionHeadResetPassword, 
    APIInstitutionHeadVerifyResetToken 
} from '../apiServices/APIs';

const StyledPaper = styled(Paper)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 450,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
}));

const UniversityHeadResetPassword = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    const [searchParams] = useSearchParams();
    
    const [formData, setFormData] = useState({
        password: '',
        password_confirmation: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [, setTokenValid] = useState(false);
    const [email, setEmail] = useState('');

    useEffect(() => {
        const token = searchParams.get('token');
        const emailParam = searchParams.get('email');

        if (!token || !emailParam) {
            showToast('Error', 'Invalid reset link', 'error');
            navigate('/institution-head/forgot-password');
            return;
        }
        setEmail(emailParam);
        verifyToken(token, emailParam);
    }, [searchParams]);

    const verifyToken = async (token, email) => {
        try {
            const response = await axios.post(APIInstitutionHeadVerifyResetToken, {
                token,
                email
            });

            if (response.data.status === 'success') {
                setTokenValid(true);
            } else {
                showToast('Error', response.data.message || 'Invalid or expired reset link', 'error');
                navigate('/institution-head/forgot-password');
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Invalid or expired reset link', 'error');
            navigate('/institution-head/forgot-password');
        } finally {
            setVerifying(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Simplified validation: only ensure fields are not empty and match
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        
        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setLoading(true);
        
        try {
            const response = await axios.post(APIInstitutionHeadResetPassword, {
                email,
                token: searchParams.get('token'),
                password: formData.password,
                password_confirmation: formData.password_confirmation
            });
            
            if (response.data.status === 'success') {
                setSubmitted(true);
                showToast('Success', response.data.message, 'success');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Failed to reset password', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
            }}>
                <CircularProgress sx={{ color: 'white' }} />
                <Typography sx={{ ml: 2, color: 'white' }}>Verifying reset link...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 2 : 3,
        }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    background: `${theme.palette.common.auth}`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 100%)',
                    }
                }}
            />

            <Fade in={true} timeout={800}>
                <StyledPaper elevation={0}>
                    {!submitted ? (
                        <>
                            <Zoom in={true} timeout={500}>
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <InstitutionHeadIcon 
                                            sx={{ 
                                                fontSize: 48, 
                                                color: theme.palette.warning.main,
                                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                borderRadius: '50%',
                                                p: 1.5
                                            }} 
                                        />
                                    </Box>
                                    <Typography
                                        variant={isMobile ? 'h5' : 'h4'}
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 1,
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                        }}
                                    >
                                        Create New Password
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Please enter your new password for your Institutional Research Lead account
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        <strong>Email:</strong> {email}
                                    </Typography>
                                </Box>
                            </Zoom>

                            <motion.div
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <Box component="form" onSubmit={handleSubmit}>
                                    <TextField
                                        fullWidth
                                        name="password"
                                        label="New Password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        error={!!errors.password}
                                        helperText={errors.password}
                                        margin="normal"
                                        disabled={loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: theme.palette.warning.main }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ 
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                            }
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        name="password_confirmation"
                                        label="Confirm New Password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={formData.password_confirmation}
                                        onChange={handleInputChange}
                                        error={!!errors.password_confirmation}
                                        helperText={errors.password_confirmation}
                                        margin="normal"
                                        disabled={loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Lock sx={{ color: theme.palette.warning.main }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ 
                                            mb: 3,
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                            }
                                        }}
                                    />

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        type="submit"
                                        disabled={loading}
                                        sx={{
                                            py: 1.5,
                                            mb: 2,
                                            fontSize: '1rem',
                                            borderRadius: 2,
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                            }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                                    </Button>

                                    <Button
                                        fullWidth
                                        variant="text"
                                        startIcon={<ArrowBack />}
                                        onClick={() => navigate('/login')}
                                        sx={{ 
                                            textTransform: 'none',
                                            color: theme.palette.warning.main,
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            }
                                        }}
                                    >
                                        Back to Login
                                    </Button>
                                </Box>
                            </motion.div>
                        </>
                    ) : (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Password Reset Successful!
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Your Institutional Research Lead account password has been changed successfully.<br />
                                    Redirecting you to login...
                                </Typography>
                                <LinearProgress sx={{ mb: 2 }} />
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate('/login')}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                        '&:hover': {
                                            background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                        }
                                    }}
                                >
                                    Go to Login
                                </Button>
                            </Box>
                        </motion.div>
                    )}
                </StyledPaper>
            </Fade>
        </Box>
    );
};

export default UniversityHeadResetPassword;